/* eslint-disable no-param-reassign, no-eval */
import React, { Component } from "react";
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import ComponentMapping from "@alboom/custom-page-components";
import Icon from "@alboompro/boom-components/general/icon";

import { connectStore } from "store/ElementDataManager/";

import {
  updateElement,
  deleteElement,
  clearSection,
} from "store/actions/pageActions";
import {
  findElement,
  findByFather,
  findByComponentName,
} from "store/selectors/pageSelector";
import DeleteModal from "shared/components/DeleteModal";
import EditorElements, { Group } from "editor/components/EditorElements";
import EditorTabs from "editor/components/EditorTabs";

import { getComponentManifest } from "shared/utils";

import { EditorPanelContainer, ButtonWrapper } from "./styles";

class EditorPanel extends Component {
  state = {
    deleteModal: false,
  };

  /**
   * When we receive an update from any element being edited,
   * update our element in redux
   *
   * @param {Mixed} updatedProps Array or object of changes executed.
   */
  updateProps = (updatedProps) => {
    const { updateElement, selected, findElement } = this.props;
    const selectedElement = findElement(selected.key);

    // This is ugly, but... if we don't receive an array, just convert
    // into one.
    const updatedPropsArray = Array.isArray(updatedProps)
      ? updatedProps
      : [updatedProps];

    // Then we reduce it to an object containing prop:value notation
    // in order to update or redux.
    const newProps = updatedPropsArray.reduce((curr, acc) => {
      return { ...curr, [acc.prop]: acc.value };
    }, {});

    const updatePayload = {
      ...selectedElement,
      props: {
        ...selectedElement.props,
        ...newProps,
      },
    };
    updateElement(updatePayload);
  };

  renderProp = (prop, currentValue, currentType = null) => {
    const { selected, findElement, locale } = this.props;

    // if conditions not be valid, not render
    if (
      prop.condition &&
      !this.checkPropCondition(selected.props, prop.condition)
    ) {
      return null;
    }

    // if we're rendering a group, skip all logic and render it.
    if (prop.group) {
      const selectedElement = findElement(selected.key);
      return (
        <div key={prop.name}>
          <Group
            {...prop}
            renderPropFn={this.renderProp}
            currentProps={selectedElement.props}
            updaterFn={this.updateProps}
          />
        </div>
      );
    }

    // We try to use one of custom fields from Editor elements
    // based on what we receive from the manifest file. If we can't, it means
    // it's a generic field so we just use GenericElement passing the props.
    let Element;

    if (prop.customElement) {
      const { component, version } = selected;
      Element =
        ComponentMapping[component][`V${version}`].customElements[prop.element];
    } else {
      Element = EditorElements[prop.element] || EditorElements.GenericElement;
    }

    // If element needs to use editorData, load it and pass
    // as props.
    if (prop.connected) {
      Element = connectStore(Element);
    }

    return (
      <div key={prop.name}>
        <Element
          locale={locale}
          updaterFn={this.updateProps}
          currentValue={currentValue}
          currentType={currentType}
          elementKey={selected.key}
          {...prop}
        />
      </div>
    );
  };

  deleteItem = () => {
    const {
      selected,
      deleteElement,
      findByComponentName,
      clearSection,
    } = this.props;

    const fn = () => {
      this.unSelect();
      deleteElement(selected.key);
      this.setState({ deleteModal: false });
    };

    // If it's a section, we need to check to see if it's the last
    // section in the page, if it is, we can't delete it or else we won't
    // be able to add elements to the page... so we clear it.
    if (selected.component === "Section") {
      const sections = findByComponentName("Section");
      if (sections.length === 1) {
        clearSection(selected.key);
        this.unSelect();
        this.setState({ deleteModal: false });
      } else {
        fn();
      }
    } else {
      fn();
    }
  };

  /**
   * When deleting an element from the editor panel (and for the time being
   * only the section will be deletable this way), if it's a section or
   * column, we need to check if it has any child. if it does, display a
   * confirmation modal for deleting it, else just delete it.
   *
   * @param {DOMEvent} e Click event fired by the dom
   */
  handleDelete = (e) => {
    e.preventDefault();
    const { selected, findByFather } = this.props;
    const children = findByFather(selected.key);

    // if it's section, we need to check if their children
    // are all empty before allowing deletion without confirmation
    if (selected.component === "Section") {
      const hasContent = children.some((children) => {
        return children.components.length;
      });
      if (hasContent) {
        this.setState({ deleteModal: true });
      } else {
        this.deleteItem();
      }
    } else if (children.length > 1) {
      this.setState({ deleteModal: true });
    } else {
      this.deleteItem();
    }
  };

  renderForm = () => {
    const { selected, findElement, locale } = this.props;
    const selectedElement = findElement(selected.key);
    const { component, version } = selectedElement;
    const { manifest } = ComponentMapping[component][`V${version || 1}`];
    const { deletable, name } = manifest;
    const props = selectedElement.props || {};

    return (
      <div>
        <EditorTabs
          manifest={manifest}
          render={(activeProps) => {
            return activeProps.map((prop) => {
              const currentProp = props[prop.name];
              const currentPropType = props[`${prop.name}Type`];
              const currentValue =
                currentProp !== undefined ? currentProp : prop.defaultValue;
              const currentType =
                currentPropType !== undefined ? currentPropType : null;
              return this.renderProp(prop, currentValue, currentType);
            });
          }}
        />
        {deletable && (
          <ButtonWrapper
            onClick={this.handleDelete}
            style={{ backgroundColor: "transparent", margin: "0 auto" }}
            textStyle={{
              color: "#e83535",
              textDecoration: "underline",
              fontSize: "12px",
            }}
            iconStyle={{ marginRight: "5px" }}
            icon={
              <Icon
                kind="bold"
                group="interface-essential"
                category="delete"
                file="bin-1.svg"
                size="16"
                color="#E83535"
              />
            }
          >
            {I18n.t("general.delete")} {name[locale]}
          </ButtonWrapper>
        )}
      </div>
    );
  };

  unSelect = (e) => {
    const doc = document.querySelector("iframe.editor-frame").contentWindow
      .document;
    doc.body.click();
  };

  getComponentDisplayName = () => {
    const { selected, locale } = this.props;
    if (selected) {
      const manifest = getComponentManifest(
        selected.component,
        `V${selected.version}`
      );
      return manifest.name[locale];
    }
    return null;
  };

  checkPropCondition = (props, condition) => {
    return condition && eval(condition);
  };

  render() {
    const { selected } = this.props;
    const { deleteModal } = this.state;

    return (
      <EditorPanelContainer>
        <ButtonWrapper
          onClick={this.unSelect}
          style={{
            width: "100%",
            backgroundColor: "#fff",
            padding: "15px",
            height: "auto",
          }}
          iconStyle={{ marginRight: 15 }}
          textStyle={{ marginRight: "auto", fontSize: "14px" }}
          icon={
            <Icon
              kind="bold"
              group="arrows-diagrams"
              category="arrows"
              file="arrow-button-left.svg"
              size="16"
              color="#212121"
            />
          }
        >
          {I18n.t("editPanel.editElement", {
            element: this.getComponentDisplayName(),
          })}
        </ButtonWrapper>
        <div>
          {selected && this.renderForm()}
          {selected && (
            <DeleteModal
              onConfirm={this.deleteItem}
              onCancel={() => {
                this.setState({ deleteModal: false });
              }}
              isOpen={deleteModal}
              element={selected.component}
            />
          )}
        </div>
      </EditorPanelContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  selected: state.editor.selected,
  locale: state.i18n.locale,
  findElement: (key) => findElement(state, key),
  findByFather: (id) => findByFather(state.page.components, id),
  findByComponentName: (name) =>
    findByComponentName(state.page.components, name),
});

const mapDispatchToProps = {
  deleteElement,
  updateElement,
  clearSection,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditorPanel);
